/* eslint-disable max-lines */

import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, $getRoot, $insertNodes, ParagraphNode } from 'lexical';
import * as React from 'react';
import { useLayoutEffect } from 'react';

type HookProps = {
  content: string;
  edited?: boolean;
  insertQuotes?: boolean;
  setTextContent?: React.Dispatch<React.SetStateAction<string>>;
};

const LoadHtmlPlugin: React.FC<HookProps> = ({
  content,
  edited = false,
  insertQuotes = false,
  setTextContent,
}): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();

  useLayoutEffect(() => {
    if (content && editor) {
      editor.update(() => {
        // In the browser you can use the native DOMParser API to parse the HTML string.
        const parser = new DOMParser();
        const dom = parser.parseFromString(content, 'text/html');

        // Once you have the DOM instance it's easy to generate LexicalNodes.
        const nodes = $generateNodesFromDOM(editor, dom);

        // Select the root
        const root = $getRoot();
        root.select();
        root.clear();
        root.getFirstDescendant()?.remove();

        // Insert them at a selection.
        $insertNodes(nodes);

        if (edited) {
          const editedNode = $createTextNode(' (edited)');
          editedNode.setStyle('color: #858585');
          const insertPlace = root.getLastDescendant() as ParagraphNode;

          if (insertPlace.__parent === 'root') {
            insertPlace.append(editedNode);
          } else {
            insertPlace.insertAfter(editedNode);
          }
        }

        if (insertQuotes) {
          const beforeNode = $createTextNode('"');
          const afterNode = $createTextNode('"');
          const insertBeforePlace = root.getFirstDescendant() as ParagraphNode;
          const insertAfterPlace = root.getLastDescendant() as ParagraphNode;

          if (insertAfterPlace.__parent === 'root') {
            insertBeforePlace.append(beforeNode);
            insertAfterPlace.append(afterNode);
          } else {
            insertBeforePlace.insertBefore(beforeNode);
            insertAfterPlace.insertAfter(afterNode);
          }
        }

        if (setTextContent) {
          setTextContent(root.getTextContent());
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  return null;
};

export default LoadHtmlPlugin;
